/**
 * Split page-preview text so the cited passage can be highlighted.
 * Matches on case-insensitive, whitespace-normalized text.
 */
export interface HighlightedPassageParts {
  before: string
  match: string
  after: string
}

export interface PassageBand {
  /** 0–100, top of highlight relative to page height */
  topPct: number
  /** 0–100, height of highlight band */
  heightPct: number
}

function normalizeWs(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/** Strip excerpt ellipsis added by resolve_anchor. */
function stripExcerptEllipsis(snippet: string): string {
  return snippet.replace(/^\.\.\./, '').replace(/\.\.\.$/, '').trim()
}

export interface ResolvedPassageOffsets {
  unitCharStart?: number | null
  unitCharEnd?: number | null
}

/**
 * Server-resolved offsets into the page's own text, when anchor resolution
 * located the passage. Preferred over matching in the browser: the server
 * matched against the same extracted text the page preview renders, so it
 * cannot mis-highlight a repeated phrase the way a client-side search can.
 */
export function passageFromOffsets(
  pageText: string,
  offsets: ResolvedPassageOffsets | null | undefined,
): HighlightedPassageParts | null {
  const start = offsets?.unitCharStart
  const end = offsets?.unitCharEnd
  if (
    typeof start !== 'number' ||
    typeof end !== 'number' ||
    start < 0 ||
    end <= start ||
    end > (pageText || '').length
  ) {
    return null
  }
  return {
    before: pageText.slice(0, start),
    match: pageText.slice(start, end),
    after: pageText.slice(end),
  }
}

export function findHighlightedPassage(
  pageText: string,
  snippet: string,
  offsets?: ResolvedPassageOffsets | null,
): HighlightedPassageParts | null {
  const fromOffsets = passageFromOffsets(pageText, offsets)
  if (fromOffsets) {
    return fromOffsets
  }

  const hayRaw = pageText || ''
  const needleRaw = stripExcerptEllipsis(snippet || '')
  if (!hayRaw || !needleRaw) {
    return null
  }

  const exactIdx = hayRaw.indexOf(needleRaw)
  if (exactIdx >= 0) {
    return {
      before: hayRaw.slice(0, exactIdx),
      match: hayRaw.slice(exactIdx, exactIdx + needleRaw.length),
      after: hayRaw.slice(exactIdx + needleRaw.length),
    }
  }

  const hay = normalizeWs(hayRaw)
  const needle = normalizeWs(needleRaw)
  if (!needle) {
    return null
  }
  const idx = hay.toLowerCase().indexOf(needle.toLowerCase())
  if (idx < 0) {
    return null
  }
  return {
    before: hay.slice(0, idx),
    match: hay.slice(idx, idx + needle.length),
    after: hay.slice(idx + needle.length),
  }
}

/**
 * Approximate a vertical highlight band on a page raster from where the
 * passage sits in the extracted page text. Not glyph-accurate — good enough
 * to draw the eye to the cited region on the image.
 */
export function estimatePassageBand(
  pageText: string,
  snippet: string,
  offsets?: ResolvedPassageOffsets | null,
): PassageBand | null {
  const parts = findHighlightedPassage(pageText, snippet, offsets)
  if (!parts) {
    return null
  }
  const total = parts.before.length + parts.match.length + parts.after.length
  if (total <= 0) {
    return null
  }
  const startPct = (parts.before.length / total) * 100
  const rawHeight = (parts.match.length / total) * 100
  // Keep the band readable: at least ~4% of page, at most ~35%.
  const heightPct = Math.min(35, Math.max(4, rawHeight + 2))
  const topPct = Math.max(0, Math.min(100 - heightPct, startPct - 1))
  return { topPct, heightPct }
}
